import { IconColor } from '@/components/ui/icons/DynamicIcon/types/colors';
import { ThemeOptions } from '@mui/material/styles/createTheme';
import { colors } from '@/styles/theme/colors';
import { getCssVariable } from '@/styles/utils/getCssVariable';

export const palette: ThemeOptions['palette'] = {
  primary: {
    //configuration/primary on design
    main: getCssVariable('--primary-main', colors.blue._700),
    light: getCssVariable('--primary-light', colors.blue._600),
    dark: getCssVariable('--primary-dark', colors.blue._750),
  },
  secondary: {
    //configuration/secondary on design
    main: getCssVariable('--secondary-main', colors.blue._750),
    deep: getCssVariable('--secondary-deep', colors.blue._550),
    light: getCssVariable('--secondary-light', colors.grey._50),
  },
  background: {
    navigation: getCssVariable('--background-navigation', colors.blue._1000),
    card: getCssVariable('--background-card', colors.grey._100),
    secondary: getCssVariable('--background-secondary', colors.white._100),
    cardHeader: getCssVariable('--background-cardHeader', colors.blue._100),
    cardRemainingDays: getCssVariable('--background-cardRemainingDays', colors.red._50),
    editMode: getCssVariable('--background-editMode', colors.blue._250),
    editModeInput: getCssVariable('--background-editModeInput', colors.blue._50),
    selectedMenuItem: getCssVariable('--background-selectedMenuItem', colors.blue._100),
    hoveredMenuItem: getCssVariable('--background-hoveredMenuItem', colors.blue._50),
    purpleMedium: getCssVariable('--background-purpleMedium', colors.purple._150),
    primary: getCssVariable('--background-primary', colors.blue._50),
  },
  tags: {
    blueLight: getCssVariable('--tags-blueLight', colors.blue._350),
    blueDark: getCssVariable('--tags-blueDark', colors.blue._800),
    deepGreenLight: getCssVariable('--tags-deepGreenLight', colors.blue._400),
    deepGreenDark: getCssVariable('--tags-deepGreenDark', colors.blue._850),
    deepGreen: getCssVariable('--tags-deepGreen', colors.blue._400),
    redLight: getCssVariable('--tags-redLight', colors.red._150),
    redDark: getCssVariable('--tags-redDark', colors.red._450),
    greyLight: getCssVariable('--tags-greyLight', colors.grey._200),
    greyDark: getCssVariable('--tags-greyDark', colors.grey._750),
    staticGreyDark: getCssVariable('--tags-staticGreyDark', colors.grey._600),
    greenLight: getCssVariable('--tags-greenLight', colors.green._300),
    greenDark: getCssVariable('--tags-greenDark', colors.green._550),
    purpleLight: getCssVariable('--tags-purpleLight', colors.purple._100),
    purpleDark: getCssVariable('--tags-purpleDark', colors.purple._400),
    black: getCssVariable('--tags-black', colors.blue._1000),
  },
  table: {
    text: {
      secondaryLight: getCssVariable('--table-text-secondary-light', colors.grey._600),
      deepBlue: getCssVariable('--table-text-deep-blue', colors.blue._800),
    },
    surface: {
      primary: getCssVariable('--table-surface-primary', colors.grey._50),
      grayLight: getCssVariable('--table-surface-grey-light', colors.grey._200),
    },
  },
  success: {
    contrastText: getCssVariable('--success-contrastText', colors.white._100),
    main: getCssVariable('--success-main', colors.green._750),
    dark: getCssVariable('--success-dark', colors.green._500),
    light: getCssVariable('--success-light', colors.green._400),
    darkText: getCssVariable('--success-darkText', colors.green._550),
    lightBackground: getCssVariable('--success-lightBackground', colors.green._50),
  },
  error: {
    contrastText: getCssVariable('--error-contrastText', colors.white._100),
    main: getCssVariable('--error-main', colors.red._300),
    light: getCssVariable('--error-light', colors.red._250),
    dark: getCssVariable('--error-dark', colors.red._350),
    darkText: getCssVariable('--error-darkText', colors.red._450),
    lightBackground: getCssVariable('--error-lightBackground', colors.red._100),
  },
  validation: {
    error: getCssVariable('--error-dark', colors.red._350),
    warning: getCssVariable('--error-dark', colors.orange._450),
    info: getCssVariable('--error-dark', colors.blue._700),
  },
  warning: {
    contrastText: getCssVariable('--warning-contrastText', colors.grey._1000),
    main: getCssVariable('--warning-main', colors.orange._350),
    dark: getCssVariable('--warning-dark', colors.orange._400),
    light: getCssVariable('--warning-light', colors.orange._300),
    darkText: getCssVariable('--warning-darkText', colors.orange._550),
    lightBackground: getCssVariable('--warning-lightBackground', colors.orange._50),
  },
  inputField: {
    default: getCssVariable('--inputField', colors.grey._600),
    hover: getCssVariable('--inputField', colors.blue._300),
    focused: getCssVariable('--inputField', colors.blue._700),
    error: getCssVariable('--inputField', colors.red._300),
    fillLight: getCssVariable('--inputField', colors.grey._150),
    fillDark: getCssVariable('--inputField', colors.grey._850),
  },
  skeleton: {
    default: getCssVariable('--skeleton-default', colors.grey._150),
  },
  other: {
    notification: getCssVariable('--other-notification', colors.red._400),
    divider: getCssVariable('--other-divider', colors.grey._650),
    information: getCssVariable('--other-information', colors.grey._450),
    informationLight: getCssVariable('--other-informationLight', colors.grey._250),
    avatar: getCssVariable('--other-avatar', colors.grey._350),
    white: getCssVariable('--other-white', colors.white._100),
    black: getCssVariable('--other-black', colors.grey._1000),
    header: getCssVariable('--other-header', colors.blue._1000),
    headerSearch: getCssVariable('--other-headerSearch', colors.grey._850),
    text: getCssVariable('--other-text', colors.white._100),
    blueHover: getCssVariable('--other-blueHover', colors.blue._200),
    searchBackdrop: getCssVariable('--other-searchBackdrop', colors.transparent._350),
    greyHover: getCssVariable('--other-greyHover', colors.grey._150),
    warningBackground: getCssVariable('--other-warningBackground', colors.orange._50),
    successBackground: getCssVariable('--other-successBackground', colors.green._50),
    errorBackground: getCssVariable('--other-errorBackground', colors.red._100),
    darkSecondary: getCssVariable('--other-darkSecondary', colors.black._50),
    editModeBorder: getCssVariable('--other-editModeBorder', colors.blue._300),
    editModeIcon: getCssVariable('--other-editModeIcon', colors.grey._600),
    selectedDate: getCssVariable('--other-selectedDate', colors.blue._650),
    contrastColor: getCssVariable('--other-contrastColor', colors.grey._1000),
    dashedBorder: getCssVariable('--other-dashedBorder', colors.grey._500),
    boxShadow: getCssVariable('--other-boxShadow', colors.transparent._200),
    lightBorder: getCssVariable('--other-lightBorder', colors.blue._350),
  },
  button: {
    lightBackground: getCssVariable('--button-lightBackground', colors.blue._150),
    border: getCssVariable('--button-border', colors.blue._550),
    disabled: getCssVariable('--button-disabled', colors.grey._450),
    background: getCssVariable('--button-background', colors.blue._150),
    main: getCssVariable('--button-main', colors.blue._700),
    switchBackground: getCssVariable('--button-switchBackground', colors.grey._50),
  },
  surface: {
    white: getCssVariable('--surface-white', colors.grey._150),
    primary: getCssVariable('--surface-primary', colors.grey._50),
    secondary: getCssVariable('--surface-secondary', colors.grey._100),
    blue: getCssVariable('--surface-blue', colors.blue._250),
    transparentLight: getCssVariable('--surface-transparent-light', colors.transparent._200),
    transparent: getCssVariable('--surface-transparent', colors.transparent._300),
    greyInverted: getCssVariable('--surface-grey-inverted', colors.grey._150),
  },
  accent: {
    secondary: getCssVariable('--accent-secondary', colors.blue._100),
    primary: getCssVariable('--accent-primary', colors.blue._700),
    primaryDarkStatic: getCssVariable('--accent-primaryDarkStatic', colors.blue._750),
    primaryStatic: getCssVariable('--accent-primaryStatic', colors.blue._700),
  },
  configuration: {
    primary: {
      main: getCssVariable('--configuration-primary-main', colors.blue._700),
      deep: getCssVariable('--configuration-primary-deep', colors.blue._550),
      light: getCssVariable('--configuration-primary-light', colors.blue._150),
    },
    secondary: {
      main: getCssVariable('--configuration-secondary-main', colors.blue._750),
      deep: getCssVariable('--configuration-deep-main', colors.blue._550),
      light: getCssVariable('--configuration-light-main', colors.grey._50),
    },
  },
  information: {
    orangeDarkInverted: getCssVariable('--information-orangeDarkInverted', colors.orange._50),
    orangeLight: getCssVariable('--information-orangeLight', colors.orange._100),
    yellowLight: getCssVariable('--information-yellowLight', colors.yellow._50),
    redLight: getCssVariable('--information-redLight', colors.red._100),
    redDarkInverted: getCssVariable('--information-redDarkInverted', colors.red._150),
    red: getCssVariable('--information-purple', colors.red._350),
    grey: getCssVariable('--information-grey', colors.grey._450),
    greyLight: getCssVariable('--information-greyLight', colors.grey._200),
    blueLight: getCssVariable('--information-blueLight', colors.blue._150),
    deepBlue: getCssVariable('--information-deepBlue', colors.blue._350),
    pinkLight: getCssVariable('--information-pinkLight', colors.pink._50),
    deepGreen: getCssVariable('--information-deepGreen', colors.blue._400),
    greenInvertedStatic: getCssVariable('--information-greenInvertedStatic', colors.green._300),
    purple: getCssVariable('--information-purple', colors.purple._100),
  },
  border: {
    blue: getCssVariable('--border-blue', colors.blue._700),
    deepBlue: getCssVariable('--border-deepBlue', colors.blue._300),
    deepBlueDark: getCssVariable('--border-deepBlueDark', colors.blue._350),
    orange: getCssVariable('--border-orange', colors.orange._400),
    orangeDark: getCssVariable('--border-orangeDark', colors.orange._450),
    red: getCssVariable('--border-red', colors.red._350),
    blueLight: getCssVariable('--border-blueLight', colors.blue._550),
    whiteInverted: getCssVariable('--border-whiteInverted', colors.grey._450),
    grey: getCssVariable('--border-grey', colors.grey._250),
    redLight: getCssVariable('--border-red-light', colors.red._200),
  },
  icon: {
    [IconColor.Info]: getCssVariable(`--icon-${IconColor.Info}`, colors.blue._700),
    [IconColor.VectorAdaptiveDark]: getCssVariable(`--icon-${IconColor.VectorAdaptiveDark}`, colors.grey._1000),
    [IconColor.VectorAdaptivePrimary]: getCssVariable(`--icon-${IconColor.VectorAdaptivePrimary}`, colors.grey._750),
    [IconColor.VectorAdaptiveTertiaryDark]: getCssVariable(
      `--icon-${IconColor.VectorAdaptiveTertiaryDark}`,
      colors.grey._400,
    ),
    [IconColor.VectorAdaptiveBlueLight]: getCssVariable(
      `--icon-${IconColor.VectorAdaptiveBlueLight}`,
      colors.blue._700,
    ),
    [IconColor.VectorAdaptiveBlueDark]: getCssVariable(
      `--dark-icon-${IconColor.VectorAdaptiveBlueDark}`,
      colors.blue._800,
    ),
    [IconColor.VectorAdaptiveOrange]: getCssVariable(`--icon-${IconColor.VectorAdaptiveOrange}`, colors.orange._400),
    [IconColor.VectorAdaptiveRedLight]: getCssVariable(`--icon-${IconColor.VectorAdaptiveRedLight}`, colors.red._350),
    [IconColor.VectorAdaptiveWhite]: getCssVariable(`--icon-${IconColor.VectorAdaptiveWhite}`, colors.grey._50),
    [IconColor.VectorStaticWhite]: getCssVariable(`--icon-${IconColor.VectorStaticWhite}`, colors.grey._50),
    [IconColor.VectorAdaptiveGreyInverted]: getCssVariable(
      `--icon-${IconColor.VectorAdaptiveGreyInverted}`,
      colors.grey._50,
    ),
    [IconColor.VectorAdaptiveGrey]: getCssVariable(`--icon-${IconColor.VectorAdaptiveGrey}`, colors.grey._500),
    [IconColor.VectorAdaptivePurple]: getCssVariable(`--icon-${IconColor.VectorAdaptivePurple}`, colors.purple._400),
    [IconColor.VectorAdaptiveGreenDark]: getCssVariable(
      `--icon-${IconColor.VectorAdaptiveGreenDark}`,
      colors.green._750,
    ),
    [IconColor.VectorAdaptiveSecondaryLight]: getCssVariable(
      `--icon-${IconColor.VectorAdaptiveSecondaryLight}`,
      colors.grey._600,
    ),
    [IconColor.VectorAdaptiveGreenLight]: getCssVariable(
      `--icon-${IconColor.VectorAdaptiveGreenLight}`,
      colors.green._450,
    ),
    [IconColor.VectorAdaptiveDeepGreen]: getCssVariable(
      `--icon-${IconColor.VectorAdaptiveDeepGreen}`,
      colors.blue._850,
    ),
  },
  text: {
    primary: getCssVariable('--text-primary', colors.grey._1000),
    primaryInverted: getCssVariable('--text-primary-inverted', colors.grey._50),
    secondary: getCssVariable('--text-secondary', colors.grey._600),
    tertiary: getCssVariable('--text-tertiary', colors.grey._400),
    disabled: getCssVariable('--text-disabled', colors.grey._500),
    hint: getCssVariable('--text-hint', colors.grey._500),
    link: getCssVariable('--text-link', colors.blue._700),
    light: getCssVariable('--text-light', colors.grey._50),
    grey: getCssVariable('--text-grey', colors.grey._500),
    greyDark: getCssVariable('--text-greyDark', colors.grey._750),
    status: getCssVariable('--text-status', colors.grey._600),
    staticWhite: getCssVariable('--text-staticWhite', colors.grey._50),
    white: getCssVariable('--text-white', colors.grey._50),
    blue: getCssVariable('--text-blue', colors.blue._750),
    blueLight: getCssVariable('--text-blueLight', colors.blue._700),
    staticBlue: getCssVariable('--text-staticBlue', colors.blue._750),
    deepBlue: getCssVariable('--text-deepBlue', colors.blue._800),
    greenDark: getCssVariable('--text-greenDark', colors.green._750),
    greenLight: getCssVariable('--text-greenLight', colors.green._450),
    greenLightInverted: getCssVariable('--text-greenLightInverted', colors.green._550),
    deepGreen: getCssVariable('--text-deepGreen', colors.blue._850),
    red: getCssVariable('--text-red', colors.red._350),
    orange: getCssVariable('--text-orange', colors.orange._450),
    orangeDark: getCssVariable('--text-orangeDark', colors.orange._450),
    pink: getCssVariable('--text-pink', colors.pink._300),
    pinkInverted: getCssVariable('--text-pinkInverted', colors.red._450),
    purple: getCssVariable('--text-purple', colors.purple._400),
    yellowDark: getCssVariable('--text-yellowDark', colors.yellow._250),
  },
  chips: {
    background: {
      blue: getCssVariable('--chips-background-blue', colors.blue._350),
      red: getCssVariable('--chips-background-red', colors.red._150),
      deepGreen: getCssVariable('--chips-background-deepGreen', colors.blue._400),
      greenDark: getCssVariable('--chips-background-greenDark', colors.green._200),
      purple: getCssVariable('--chips-background-purple', colors.purple._100),
      pink: getCssVariable('--chips-background-pink', colors.pink._50),
      orange: getCssVariable('--chips-background-orange', colors.orange._150),
      yellow: getCssVariable('--chips-background-yellow', colors.yellow._50),
      grey: getCssVariable('--chips-background-greyLight', colors.grey._200),
    },
    text: {
      blue: getCssVariable('--chips-text-blue', colors.blue._800),
      red: getCssVariable('--chips-text-red', colors.red._450),
      green: getCssVariable('--chips-text-green', colors.green._550),
      deepGreen: getCssVariable('--chips-text-deepGreen', colors.blue._850),
      purple: getCssVariable('--chips-text-purple', colors.purple._400),
      pink: getCssVariable('--chips-text-pink', colors.pink._300),
      orange: getCssVariable('--chips-text-orange', colors.orange._450),
      yellow: getCssVariable('--chips-text-yellow', colors.yellow._250),
      greyLight: getCssVariable('--chips-text-greyLight', colors.black._50),
    },
    border: {
      blue: getCssVariable('--chips-border-blue', colors.blue._800),
      red: getCssVariable('--chips-border-red', colors.red._450),
      deepGreen: getCssVariable('--chips-border-deepGreen', colors.blue._850),
      green: getCssVariable('--chips-border-green', colors.green._550),
      purple: getCssVariable('--chips-border-purple', colors.purple._400),
      pink: getCssVariable('--chips-border-pink', colors.pink._300),
      orange: getCssVariable('--chips-border-orange', colors.orange._450),
      yellow: getCssVariable('--chips-border-yellow', colors.yellow._250),
      grey: getCssVariable('--chips-border-grey', colors.black._50),
    },
  },
};
